import { useCallback, useState } from 'react';

interface ImageUploaderProps {
  onImageSelect: (file: File, dataUrl: string) => void;
  compact?: boolean;
  thumbnailMode?: boolean;
}

export default function ImageUploader({ onImageSelect, compact = false, thumbnailMode = false }: ImageUploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      onImageSelect(file, dataUrl);
    };
    reader.readAsDataURL(file);
  }, [onImageSelect]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFile(file);
      }
    };
    input.click();
  }, [handleFile]);

  // Thumbnail mode - fills container with card aspect ratio
  if (thumbnailMode) {
    return (
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className="create-thumbnail"
        style={{
          border: `1px dashed ${isDragOver ? 'var(--color-text)' : 'var(--color-border)'}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: isDragOver ? 'var(--color-surface)' : 'transparent',
          transition: 'all 0.2s ease',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p style={{
          color: 'var(--color-text-muted)',
          fontSize: '10px',
          margin: '6px 0 0 0',
          fontFamily: "'Host Grotesk', sans-serif",
          textAlign: 'center',
          lineHeight: 1.3,
        }}>
          Drop or click<br />to upload
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${isDragOver ? 'var(--color-text)' : 'var(--color-border)'}`,
          borderRadius: '8px',
          padding: '20px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragOver ? 'var(--color-surface)' : 'transparent',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <p style={{
          color: 'var(--color-text-muted)',
          fontSize: '13px',
          margin: 0,
          fontFamily: "'Host Grotesk', sans-serif",
        }}>
          Drop or click to upload
        </p>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      style={{
        border: `2px dashed ${isDragOver ? 'var(--color-text)' : 'var(--color-border)'}`,
        borderRadius: '12px',
        padding: '40px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: isDragOver ? 'var(--color-surface)' : 'transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>
      <p style={{
        color: 'var(--color-text)',
        fontSize: '16px',
        margin: '0 0 8px 0',
        fontFamily: "'Host Grotesk', sans-serif",
      }}>
        Drop your artwork here
      </p>
      <p style={{
        color: 'var(--color-text-muted)',
        fontSize: '14px',
        margin: 0,
        fontFamily: "'Host Grotesk', sans-serif",
      }}>
        or click to browse (PNG, JPG, WebP)
      </p>
    </div>
  );
}
